import React, { createContext, useMemo } from 'react'

import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { useGetActiveFailureFlagExperiments } from 'hooks/use-get-active-failure-flag-experiments'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { type InfraAttack } from 'types/api/attacks/details/infra-attack'
import { type FailureFlagExperiment } from 'types/api/failure-flags/failure-flags'
import { type ReliabilityTestRun } from 'types/api/reliability-tests/runs'
import { type FetchFunc } from 'types/util'
import {
  attacksActiveGet,
  reliabilityTestsActiveGet,
  scenariosRunsGet
} from 'utils/endpoints-access'
import { ms } from 'utils/millisecond'

import type { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'

export interface NowRunningContextValues {
  activeAttacks: InfraAttack[]
  isFetchingActiveAttacks: boolean
  hasErrorActiveAttacks: boolean
  refetchActiveAttacks: FetchFunc
  activeScenarioRuns: ScenarioRun[]
  isFetchingActiveScenarioRuns: boolean
  hasErrorActiveScenarioRuns: boolean
  refetchActiveScenarioRuns: FetchFunc
  activeReliabilityTests: ReliabilityTestRun[]
  isFetchingActiveReliabilityTests: boolean
  isLoadingActiveReliabilityTests: boolean
  hasErrorActiveReliabilityTests: boolean
  refetchActiveReliabilityTests: FetchFunc
  activeFailureFlagExperiments: FailureFlagExperiment[]
  isFetchingActiveFailureFlagExperiments: boolean
  hasErrorActiveFailureFlagExperiments: boolean
  refetchActiveFailureFlagExperiments: FetchFunc
  isLoading: boolean
  totalActiveTests: number
}

export const DEFAULT_NOW_RUNNING_CONTEXT_VALUES: NowRunningContextValues = {
  activeAttacks: [],
  isFetchingActiveAttacks: false,
  hasErrorActiveAttacks: false,
  refetchActiveAttacks: async () => {},
  activeScenarioRuns: [],
  isFetchingActiveScenarioRuns: false,
  hasErrorActiveScenarioRuns: false,
  refetchActiveScenarioRuns: async () => {},
  activeReliabilityTests: [],
  isFetchingActiveReliabilityTests: false,
  isLoadingActiveReliabilityTests: false,
  hasErrorActiveReliabilityTests: false,
  refetchActiveReliabilityTests: async () => {},
  activeFailureFlagExperiments: [],
  isFetchingActiveFailureFlagExperiments: false,
  hasErrorActiveFailureFlagExperiments: false,
  refetchActiveFailureFlagExperiments: async () => {},
  isLoading: true,
  totalActiveTests: 0
}

export const NowRunningContext = createContext<NowRunningContextValues>(
  DEFAULT_NOW_RUNNING_CONTEXT_VALUES
)

export function useNowRunningContext() {
  return React.useContext(NowRunningContext)
}

const REFETCH_INTERVAL = ms.seconds(10)
const STALE_TIME = REFETCH_INTERVAL

export const NowRunningContextProvider: React.FC = ({ children }) => {
  const {
    currentTeamId,
    hasEndpointAccess,
    hasFailureFlags,
    hasFaultInjection,
    hasReliabilityManagement
  } = useAuth()

  const { nowRunning: isNowRunningEnabled } = useFeatureFlags()

  const {
    data: activeAttacks = [],
    isFetching: isFetchingActiveAttacks,
    isLoading: isLoadingActiveAttacks,
    isError: hasErrorActiveAttacks,
    refetch: refetchActiveAttacks
  } = useGremlinQuery<InfraAttack[]>(
    'attacks/active',
    {},
    {
      enabled:
        isNowRunningEnabled &&
        hasFaultInjection &&
        hasEndpointAccess(attacksActiveGet, { teamId: currentTeamId }),
      placeholderData: [],
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    }
  )

  const {
    data: activeReliabilityTests = [],
    isFetching: isFetchingActiveReliabilityTests,
    isLoading: isLoadingActiveReliabilityTests,
    isError: hasErrorActiveReliabilityTests,
    refetch: refetchActiveReliabilityTests
  } = useGremlinQuery<ReliabilityTestRun[]>(
    'reliability-tests/active',
    {},
    {
      enabled:
        isNowRunningEnabled &&
        hasReliabilityManagement &&
        hasEndpointAccess(reliabilityTestsActiveGet, { teamId: currentTeamId }),
      placeholderData: [],
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    }
  )

  const {
    data: activeScenarioRuns = [],
    isFetching: isFetchingActiveScenarioRuns,
    isLoading: isLoadingActiveScenarioRuns,
    isError: hasErrorActiveScenarioRuns,
    refetch: refetchActiveScenarioRuns
  } = useGremlinQuery<ScenarioRun[]>(
    'scenarios/runs?state=Active',
    {},
    {
      enabled:
        isNowRunningEnabled &&
        hasFaultInjection &&
        hasEndpointAccess(scenariosRunsGet, { teamId: currentTeamId }),
      placeholderData: [],
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    }
  )

  const {
    data: activeFailureFlagExperiments = [],
    isFetching: isFetchingActiveFailureFlagExperiments,
    isLoading: isLoadingActiveFailureFlagExperiments,
    isError: hasErrorActiveFailureFlagExperiments,
    refetch: refetchActiveFailureFlagExperiments
  } = useGetActiveFailureFlagExperiments({
    enabled: hasFailureFlags,
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL
  })

  const totalActiveTests = useMemo(() => {
    if (
      isLoadingActiveReliabilityTests ||
      isLoadingActiveScenarioRuns ||
      isLoadingActiveAttacks ||
      isLoadingActiveFailureFlagExperiments
    ) {
      return 0
    }

    return (
      activeAttacks.length +
      activeScenarioRuns.length +
      activeReliabilityTests.length +
      activeFailureFlagExperiments.length
    )
  }, [
    activeAttacks.length,
    activeFailureFlagExperiments.length,
    activeReliabilityTests.length,
    activeScenarioRuns.length,
    isLoadingActiveAttacks,
    isLoadingActiveFailureFlagExperiments,
    isLoadingActiveReliabilityTests,
    isLoadingActiveScenarioRuns
  ])

  const isLoading =
    isLoadingActiveAttacks ||
    isLoadingActiveScenarioRuns ||
    isLoadingActiveReliabilityTests ||
    isLoadingActiveFailureFlagExperiments

  const value = useMemo<NowRunningContextValues>(() => {
    return {
      activeAttacks,
      isFetchingActiveAttacks,
      hasErrorActiveAttacks,
      refetchActiveAttacks,
      activeScenarioRuns,
      isFetchingActiveScenarioRuns,
      hasErrorActiveScenarioRuns,
      refetchActiveScenarioRuns,
      activeReliabilityTests,
      isFetchingActiveReliabilityTests,
      isLoadingActiveReliabilityTests,
      hasErrorActiveReliabilityTests,
      refetchActiveReliabilityTests,
      activeFailureFlagExperiments,
      isFetchingActiveFailureFlagExperiments,
      hasErrorActiveFailureFlagExperiments,
      refetchActiveFailureFlagExperiments,
      isLoading,
      totalActiveTests
    }
  }, [
    activeAttacks,
    isFetchingActiveAttacks,
    hasErrorActiveAttacks,
    refetchActiveAttacks,
    activeScenarioRuns,
    isFetchingActiveScenarioRuns,
    isLoadingActiveReliabilityTests,
    hasErrorActiveScenarioRuns,
    refetchActiveScenarioRuns,
    activeReliabilityTests,
    isFetchingActiveReliabilityTests,
    hasErrorActiveReliabilityTests,
    refetchActiveReliabilityTests,
    activeFailureFlagExperiments,
    isFetchingActiveFailureFlagExperiments,
    hasErrorActiveFailureFlagExperiments,
    refetchActiveFailureFlagExperiments,
    isLoading,
    totalActiveTests
  ])

  return (
    <NowRunningContext.Provider value={value}>
      {children}
    </NowRunningContext.Provider>
  )
}
